import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";

import { ApiError, forgotPassword } from "../api/client";
import { AuthShell } from "../components/AuthShell";
import { isValidEmail } from "../lib/validation";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FormError } from "@/components/ui/form-error";

/**
 * Forgot-password (ADR-0010).
 *
 * Posts the email to `POST /auth/forgot-password`. The backend always
 * returns 204 regardless of whether the email matched (no
 * account-existence oracle). The UI mirrors that — a single uniform
 * confirmation message regardless of outcome (except for 429, which
 * is honest about being rate-limited).
 */
export function ForgotPasswordRoute() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => forgotPassword(email.trim().toLowerCase()),
    onSuccess: () => setSubmitted(true),
    onError: (err) => {
      if (err instanceof ApiError && err.status === 429) {
        setTopError("Too many attempts. Wait a minute and try again.");
        return;
      }
      // Any other failure: render the same uniform message we'd render
      // on success. Better UX than exposing the failure (and consistent
      // with the no-oracle backend posture).
      setSubmitted(true);
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTopError(null);
    if (!email.trim()) {
      setTopError("Enter the email you signed up with.");
      return;
    }
    // CHP-809: catch a syntactically-invalid email client-side. `noValidate`
    // is retained (below) so the uniform "Check your email" behaviour on the
    // wire is unchanged for valid-but-unknown addresses — we only short-circuit
    // a malformed entry that the backend would 422 (which onError would
    // otherwise mask as a false "link sent").
    if (!isValidEmail(email)) {
      setTopError("Enter a valid email address.");
      return;
    }
    submit.mutate();
  }

  if (submitted) {
    return (
      <AuthShell headingId="forgot-heading" heading="Check your email" bodyClassName="space-y-4">
        <div role="status" aria-live="polite" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            If an account exists for that email address, we've sent a link to reset your password.
            The link will expire after a single use.
          </p>
          <p>
            <Link to="/login" className="text-sm font-medium text-primary hover:underline">
              ← Back to sign in
            </Link>
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      headingId="forgot-heading"
      heading="Reset your password"
      subheading="Enter your work email and we'll send you a reset link."
    >
      <form onSubmit={onSubmit} aria-busy={submit.isPending} noValidate className="space-y-4">
        <div className="space-y-2">
          <Label
            htmlFor="forgot-email"
            className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground"
          >
            Work email
          </Label>
          <Input
            id="forgot-email"
            type="email"
            autoComplete="email"
            autoFocus
            aria-required="true"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {topError && <FormError>{topError}</FormError>}

        <Button
          type="submit"
          loading={submit.isPending}
          loadingLabel="Sending…"
          size="lg"
          className="w-full"
        >
          Send reset link
        </Button>
      </form>

      <p className="text-center text-sm">
        <Link to="/login" className="font-medium text-primary hover:underline">
          ← Back to sign in
        </Link>
      </p>
    </AuthShell>
  );
}
